import { FACTSET_ID_LOOKUP_ERROR_CODES } from "../shared/factset/factsetConstants";

/**
 * Listing and regional IDs are absent: an ID Lookup v2 row carries only
 * dataset, id, idType, name, entityId, and issuerName, so nothing downstream
 * can source a listing ID. Requests for one fall through to research instead
 * of spending a lookup call that cannot answer them.
 */
export type IdLookupRequestedField =
  | "ticker"
  | "entity_id"
  | "ultimate_parent_name"
  | "ultimate_parent_id"
  | "domicile_country";

export interface IdLookupRoutingIntent {
  active: boolean;
  requestedFields: IdLookupRequestedField[];
}

export interface IdLookupAssessment {
  kind: "satisfied" | "fallback_required" | "retry_lookup";
  searchText: string;
  missingFields: IdLookupRequestedField[];
  candidateCount: number;
  fallbackReason: string;
}

export interface PendingIdLookupFallback {
  key: string;
  searchText: string;
  missingFields: IdLookupRequestedField[];
}

export type IdLookupResearchDecision =
  | { kind: "normal" }
  | {
      kind: "fallback";
      pendingKeys: string[];
      missingFields: IdLookupRequestedField[];
    }
  | {
      kind: "blocked";
      reason:
        | "lookup_required"
        | "lookup_results_unseen"
        | "lookup_already_satisfied"
        | "fallback_scope_mismatch"
        | "fallback_in_progress";
      nextTool: "id_lookup" | "research" | null;
    };

export type IdLookupTerminalBlock =
  | {
      issue: "id_lookup_required";
      nextTool: "id_lookup";
      instruction: string;
    }
  | {
      issue: "id_lookup_retry_required";
      nextTool: "id_lookup";
      instruction: string;
    }
  | {
      issue: "id_lookup_fallback_required";
      nextTool: "research";
      instruction: string;
    };

const FIELD_LABELS: Record<IdLookupRequestedField, string> = {
  ticker: "ticker",
  entity_id: "entity ID",
  ultimate_parent_name: "ultimate-parent company name",
  ultimate_parent_id: "ultimate-parent entity ID",
  domicile_country: "domicile country",
};

const RETRIEVAL_VERB =
  /\b(?:find|look\s*up|lookup|resolve|identify|determine|get|give|provide|return|retrieve|show|list|tell|populate|fill|add|write|map|match)\b/i;
const DEFINITION_REQUEST =
  /\b(?:define|definition of|meaning of|what (?:does|do) .{0,30} mean|explain\s+(?:how\s+)?(?:ticker(?:\s+symbol)?s?|entity[-\s]*(?:ids?|identifiers?)|ultimate[-\s]+parents?|the\s+(?:concept|meaning))|how (?:does|do|is|are) .{0,30} work)\b/i;
const TICKER_TERM =
  /\b(?:ticker(?:\s+symbol)?s?|stock\s+symbols?|trading\s+symbols?|exchange\s+symbols?)\b/i;
/**
 * FactSet's canonical company identifier is the entity ID, so a bare "company
 * ID" resolves to it. "Listing ID" and "security ID" are deliberately not
 * recognized: the lookup carries no field that answers them, so those requests
 * belong to research rather than to a call that cannot succeed.
 */
const ENTITY_ID_TERM =
  /\b(?:factset\s+)?(?:(?:company\s+)?entity|company)[-\s]*(?:id|ids|identifier|identifiers)\b/i;
const ULTIMATE_PARENT_TERM =
  /\bultimate[-\s]+parent(?:\s+(?:company|entity|organization|organisation))?\b/i;
/**
 * The lookup carries each company's domicile country, so asking for it is an
 * identity request rather than document research. Deliberately narrow: a bare
 * "country" also appears in questions about revenue or operations by country,
 * which this field cannot answer.
 */
const DOMICILE_COUNTRY_TERM =
  /\bdomicil(?:e|ed|es|iary)\b|\bcountry\s+of\s+(?:domicile|incorporation|registration|origin)\b|\b(?:home|headquarters|hq|incorporation|registration)\s+country\b|\bincorporated\s+in\s+(?:which\s+)?country\b/i;
const ULTIMATE_PARENT_ID_TERM =
  /\bultimate[-\s]+parent(?:\s+(?:company|entity|organization|organisation))?[-\s]*(?:id|ids|identifier|identifiers)\b/i;
const ULTIMATE_PARENT_NAME_TERM =
  /\bultimate[-\s]+parent(?:\s+(?:company|entity|organization|organisation))?[-\s]*(?:name|names)\b/i;
const DIRECT_QUESTION = /\b(?:what(?:'s| is| are)|which|who(?:'s| is))\b/i;
const WORKBOOK_TARGET = /\b(?:column|columns|row|rows|cell|cells|range|worksheet|sheet)\b/i;
const WORKSHEET_LIST_TARGET =
  /\b(?:(?:each|every|all)\b.{0,50}\b(?:compan(?:y|ies)|rows?)|(?:compan(?:y|ies)|issuer|entity)\s+(?:list|table|rows?)|(?:worksheet|sheet|table)\s+(?:list|rows?)|(?:populate|fill|complete|map)\b.{0,50}\b(?:columns?|rows?|range)|\d+\s+(?:compan(?:y|ies)|rows?))\b/i;
const A1_RANGE = /\$?[A-Za-z]{1,3}\$?(\d{1,7})\s*:\s*\$?[A-Za-z]{1,3}\$?(\d{1,7})/g;
const RANGE_WRITE_VERB =
  /\b(?:fill|populate|complete|write|put|add|map|enter|paste|output)\b/i;
const PLURAL_IDENTITY_TERM =
  /\b(?:tickers|stock\s+symbols|trading\s+symbols|exchange\s+symbols|(?:entity|company|listing|security)[-\s]*(?:ids|identifiers)|ultimate[-\s]+parents)\b/i;
/**
 * Identity field names in any grammatical number. Blanking these before the
 * list check keeps "the tickers and entity IDs for Apple" — one company, three
 * fields — from reading as a list of companies.
 */
const IDENTITY_FIELD_TERM =
  /\b(?:ticker(?:\s+symbol)?s?|(?:stock|trading|exchange)\s+symbols?|(?:entity|company|listing|security)[-\s]*(?:ids?|identifiers?)|ultimate[-\s]+parents?)\b/gi;
/**
 * Separators that can join two company names. `&` is deliberately absent: it
 * sits inside company names (AT&T, S&P, Procter & Gamble) far more often than
 * between them, and users listing companies write "and", a comma, or a slash.
 */
const COMPANY_LIST_SEPARATOR =
  /(?:,|;|\/|\+|\band\b|\bor\b|\bplus\b|\bvs\.?\b|\bversus\b)/gi;
/**
 * Words that can sit beside a list separator without naming a company, so a
 * separator between two of them is punctuating a sentence rather than a list.
 */
const LIST_SEPARATOR_STOPWORD = new Set([
  "a", "add", "all", "also", "an", "and", "are", "as", "at", "be", "both",
  "by", "complete", "each", "enter", "every", "fill", "find", "for", "from",
  "get", "give", "in", "include", "into", "is", "its", "list", "look", "map",
  "me", "my", "of", "on", "or", "our", "output", "paste", "please",
  "populate", "put", "report", "resolve", "return", "show", "that", "the",
  "their", "then", "these", "this", "those", "to", "up", "us", "was", "were",
  "what", "which", "who", "with", "write",
]);
const POSSESSIVE_OWNER = /([A-Za-z0-9][A-Za-z0-9.&-]*)['’]s\b/g;
/** Possessive owners that stand in for a company instead of naming one. */
const GENERIC_POSSESSIVE_OWNERS = new Set([
  "business",
  "companies",
  "company",
  "corporation",
  "entity",
  "firm",
  "group",
  "holding",
  "holdings",
  "issuer",
  "it",
  "its",
  "parent",
  "security",
  "subsidiary",
]);
const MULTI_COMPANY_PHRASE =
  /\b(?:both\s+compan(?:y|ies)|each\s+(?:of|compan(?:y|ies))|every\s+compan(?:y|ies)|all\s+(?:of\s+)?(?:these|those|the)\s+compan(?:y|ies)|for\s+each)\b/i;
const NON_LOOKUP_OPERATION =
  /\b(?:rename|format|style|sort|filter|move|delete|remove|hide|unhide|resize|autofit|color|bold)\b/i;
const POSSESSIVE_TARGET =
  /(?:['’]s|s['’])\s+(?:ticker(?:\s+symbol)?|(?:company|entity|listing)[-\s]*(?:id|identifier)|ultimate[-\s]+parent)\b/i;
const SUPPLIED_TICKER =
  /\b(?:(?:using|use|with|for|from|based\s+on)\s+(?:(?:the|these|those)\s+)?(?:ticker(?:\s+symbol)?s?|stock\s+symbols?|trading\s+symbols?)|(?:ticker(?:\s+symbol)?s?|stock\s+symbols?|trading\s+symbols?)\s+(?:in|from)\s+(?:the\s+)?(?:cell|cells|column|columns|range|worksheet|sheet))\b/i;
const SUPPLIED_ENTITY_ID =
  /\b(?:(?:using|use|with|for|from|based\s+on)\s+(?:(?:the|these|those)\s+)?(?:company|entity)[-\s]*(?:ids?|identifiers?)|(?:company|entity)[-\s]*(?:ids?|identifiers?)\s+(?:in|from)\s+(?:the\s+)?(?:cell|cells|column|columns|range|worksheet|sheet))\b/i;

function normalizeText(value: string): string {
  return value
    .replace(/[’‘]/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Reports whether a request writes down a column or block of rows, which only
 * a company list can fill. A single-row target such as "write it to A1:B1"
 * still belongs to one company, and a range without a write verb is usually a
 * quarter label such as "Q1:Q4".
 */
function targetsMultiRowRange(text: string): boolean {
  if (!RANGE_WRITE_VERB.test(text)) return false;
  for (const match of text.matchAll(A1_RANGE)) {
    if (match[1] !== match[2]) return true;
  }
  return false;
}

/** Reports the word immediately before the end of a segment, if there is one. */
function trailingWord(segment: string): string | null {
  return /([A-Za-z0-9][A-Za-z0-9.'-]*)\s*$/.exec(segment)?.[1] ?? null;
}

function isSeparatorStopword(word: string): boolean {
  return LIST_SEPARATOR_STOPWORD.has(
    word.toLocaleLowerCase().replace(/['’]s$/, ""),
  );
}

function couldNameCompany(word: string | null): boolean {
  return !!word && !isSeparatorStopword(word);
}

/**
 * Reports whether a segment opens with something that could name a company. A
 * stopword normally rules it out, but a capitalized one followed by another
 * capitalized word opens a proper name — "The Trade Desk", "US Steel" — rather
 * than the sentence glue in "... and for Apple".
 */
function leadsCompanyName(segment: string): boolean {
  const [, first, second] =
    /^\s*([A-Za-z0-9][A-Za-z0-9.'-]*)(?:\s+([A-Za-z0-9][A-Za-z0-9.'-]*))?/.exec(
      segment,
    ) ?? [];
  if (!first) return false;
  if (!isSeparatorStopword(first)) return true;
  return /^[A-Z]/.test(first) && !!second && /^[A-Z]/.test(second);
}

/**
 * Reports whether a list separator joins two words that could each be a
 * company name. Field names are blanked first, because a separator between
 * them is listing fields rather than companies, and a separator whose
 * neighbours are ordinary sentence words is punctuation rather than a list.
 */
function joinsSeveralCompanyNames(text: string): boolean {
  const withoutFields = text.replace(IDENTITY_FIELD_TERM, " ");
  for (const match of withoutFields.matchAll(COMPANY_LIST_SEPARATOR)) {
    const index = match.index ?? 0;
    if (
      couldNameCompany(trailingWord(withoutFields.slice(0, index))) &&
      leadsCompanyName(withoutFields.slice(index + match[0].length))
    ) {
      return true;
    }
  }
  return false;
}

function distinctPossessiveOwners(text: string): Set<string> {
  const owners = new Set<string>();
  for (const match of text.matchAll(POSSESSIVE_OWNER)) {
    const owner = normalizeCompanyKey(match[1]);
    if (owner && !GENERIC_POSSESSIVE_OWNERS.has(owner)) owners.add(owner);
  }
  return owners;
}

/**
 * Reports whether a request asks the same identity question about several
 * companies. Capitalized words are never read as company names, so the signals
 * are structural: two different possessive owners where at least one carries an
 * identity field ("Apple's ticker on the NYSE and Microsoft's on NASDAQ"), or a
 * plural field name alongside a separator joining two possible company names
 * ("Apple and Microsoft tickers").
 *
 * A single possessive owner settles the question on its own, whatever else the
 * sentence lists. That is what keeps "Marks and Spencer's tickers" and "Procter
 * & Gamble's tickers" single-company: the separator belongs to the company's
 * own name.
 */
function mentionsSeveralCompanies(text: string): boolean {
  const owners = distinctPossessiveOwners(text);
  if (owners.size === 1) return false;
  if (owners.size > 1 && POSSESSIVE_TARGET.test(text)) return true;
  return PLURAL_IDENTITY_TERM.test(text) && joinsSeveralCompanyNames(text);
}

function isTargetedRetrieval(
  text: string,
  term: RegExp,
  options: { suppliedValue?: boolean } = {},
): boolean {
  if (!term.test(text)) return false;
  if (DEFINITION_REQUEST.test(text) && !RETRIEVAL_VERB.test(text)) return false;
  if (NON_LOOKUP_OPERATION.test(text) && !RETRIEVAL_VERB.test(text)) return false;

  const normalized = normalizeText(text);
  const termMatch = normalized.match(term);
  const termIndex = termMatch?.index ?? -1;
  const retrievalMatch = normalized.match(RETRIEVAL_VERB);
  const retrievalBeforeTerm =
    termIndex >= 0 &&
    retrievalMatch?.index != null &&
    retrievalMatch.index < termIndex &&
    termIndex - retrievalMatch.index <= 100;
  const directQuestion =
    DIRECT_QUESTION.test(normalized) &&
    (termIndex < 0 || termIndex <= 80 || POSSESSIVE_TARGET.test(normalized));
  const fieldLeadsTarget =
    termIndex >= 0 &&
    /^\s+(?:for|of)\b/i.test(
      normalized.slice(termIndex + (termMatch?.[0]?.length ?? 0)),
    );
  const workbookTarget =
    WORKBOOK_TARGET.test(normalized) &&
    (RETRIEVAL_VERB.test(normalized) || normalized.split(/\s+/).length <= 16);
  const shortIdentityPhrase = normalized.split(/\s+/).length <= 10;

  if (options.suppliedValue && !fieldLeadsTarget && !POSSESSIVE_TARGET.test(normalized)) {
    return false;
  }

  return (
    retrievalBeforeTerm ||
    directQuestion ||
    fieldLeadsTarget ||
    POSSESSIVE_TARGET.test(normalized) ||
    workbookTarget ||
    shortIdentityPhrase
  );
}

/**
 * Extract identity fields only when they are requested outputs, rather than
 * identifiers the user already supplied for another task.
 */
export function extractRequestedIdLookupFields(text: string): IdLookupRequestedField[] {
  if (!text.trim()) return [];
  const requested = new Set<IdLookupRequestedField>();

  if (
    isTargetedRetrieval(text, TICKER_TERM, {
      suppliedValue: SUPPLIED_TICKER.test(text),
    })
  ) {
    requested.add("ticker");
  }

  const textWithoutUltimateParentId = text.replace(ULTIMATE_PARENT_ID_TERM, "");
  if (
    isTargetedRetrieval(textWithoutUltimateParentId, ENTITY_ID_TERM, {
      suppliedValue: SUPPLIED_ENTITY_ID.test(textWithoutUltimateParentId),
    })
  ) {
    requested.add("entity_id");
  }

  if (isTargetedRetrieval(text, DOMICILE_COUNTRY_TERM)) {
    requested.add("domicile_country");
  }

  if (isTargetedRetrieval(text, ULTIMATE_PARENT_TERM)) {
    const explicitlyRequestsId = ULTIMATE_PARENT_ID_TERM.test(text);
    const explicitlyRequestsName = ULTIMATE_PARENT_NAME_TERM.test(text);
    if (explicitlyRequestsId) requested.add("ultimate_parent_id");
    if (explicitlyRequestsName || !explicitlyRequestsId) {
      requested.add("ultimate_parent_name");
    }
  }

  return Array.from(requested);
}

export function detectIdLookupRoutingIntent(
  userRequest: string,
  supplementalText = "",
): IdLookupRoutingIntent {
  const explicitFields = extractRequestedIdLookupFields(userRequest);
  const requestedFields =
    explicitFields.length > 0
      ? explicitFields
      : extractRequestedIdLookupFields(supplementalText);
  return {
    active: requestedFields.length > 0,
    requestedFields,
  };
}

/**
 * Reports whether an identity request covers a worksheet list or several
 * companies, which routes it to the registered workload controller instead of
 * the single-company lookup path. Each signal below has to be one a single
 * company cannot emit, because a false positive sends a one-company question
 * into workload registration with no rows to register. Cardinality comes only
 * from user-authored text; generated plan text is reserved for field hints.
 */
export function detectIdLookupWorksheetWorkload(
  userRequest: string,
): boolean {
  const text = normalizeText(userRequest);
  return (
    WORKSHEET_LIST_TARGET.test(text) ||
    MULTI_COMPANY_PHRASE.test(text) ||
    targetsMultiRowRange(text) ||
    mentionsSeveralCompanies(text)
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseOutput(output: string | unknown): Record<string, unknown> | null {
  if (isRecord(output)) return output;
  if (typeof output !== "string") return null;
  try {
    const parsed = JSON.parse(output) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function readString(record: Record<string, unknown> | null, key: string): string {
  const value = record?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function normalizeCompanyKey(value: string): string {
  return normalizeText(value)
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function validationFailure(output: Record<string, unknown>): boolean {
  const errorCode = readString(output, "error_code");
  if (
    errorCode === FACTSET_ID_LOOKUP_ERROR_CODES.INVALID_REQUEST ||
    errorCode === FACTSET_ID_LOOKUP_ERROR_CODES.MISSING_SEARCH_TEXT ||
    errorCode === FACTSET_ID_LOOKUP_ERROR_CODES.SEARCH_TEXT_TOO_LARGE
  ) {
    return true;
  }
  const error = readString(output, "error");
  return /(?:missing required parameter|unknown id_lookup field|must be an|must be an? integer|must be an? array|is not supported|cannot be present|exceeds the .*byte limit)/i.test(
    error,
  );
}

function candidateHasField(
  candidate: Record<string, unknown>,
  field: IdLookupRequestedField,
): boolean {
  const nonEmpty = (key: string): boolean => readString(candidate, key).length > 0;
  switch (field) {
    case "ticker":
      return nonEmpty("ticker");
    case "entity_id":
      return nonEmpty("entity_id");
    case "ultimate_parent_name":
      return nonEmpty("ultimate_parent_company_name");
    case "ultimate_parent_id":
      return nonEmpty("ultimate_parent_entity_id");
    case "domicile_country":
      return nonEmpty("country");
  }
}

export function assessIdLookupResult(
  rawArgs: unknown,
  rawOutput: string | unknown,
  requestedFields: readonly IdLookupRequestedField[],
): IdLookupAssessment {
  const args = isRecord(rawArgs) ? rawArgs : null;
  const output = parseOutput(rawOutput);
  const searchText =
    readString(output, "search_text") || readString(args, "search_text");

  if (!output) {
    return {
      kind: "fallback_required",
      searchText,
      missingFields: [...requestedFields],
      candidateCount: 0,
      fallbackReason: "invalid_lookup_output",
    };
  }

  if (validationFailure(output)) {
    return {
      kind: "retry_lookup",
      searchText,
      missingFields: [...requestedFields],
      candidateCount: 0,
      fallbackReason: "invalid_lookup_arguments",
    };
  }

  const candidates = Array.isArray(output.candidates)
    ? output.candidates.filter(isRecord)
    : [];
  if (output.success === false || output.status === "error") {
    return {
      kind: "fallback_required",
      searchText,
      missingFields: [...requestedFields],
      candidateCount: candidates.length,
      fallbackReason:
        readString(output, "fallback_reason") ||
        readString(output, "error_code") ||
        "id_lookup_failed",
    };
  }

  if (candidates.length === 0) {
    return {
      kind: "fallback_required",
      searchText,
      missingFields: [...requestedFields],
      candidateCount: 0,
      fallbackReason: readString(output, "fallback_reason") || "no_match",
    };
  }

  const hasCompleteCandidate = candidates.some((candidate) =>
    requestedFields.every((field) => candidateHasField(candidate, field)),
  );
  if (!hasCompleteCandidate) {
    return {
      kind: "fallback_required",
      searchText,
      // The requested fields exist at best as a union across candidates, and
      // combining records would describe a company that does not exist, so the
      // whole requested identity stays unresolved.
      missingFields: [...requestedFields],
      candidateCount: candidates.length,
      fallbackReason:
        readString(output, "fallback_reason") || "requested_fields_unavailable",
    };
  }

  return {
    kind: "satisfied",
    searchText,
    missingFields: [],
    candidateCount: candidates.length,
    fallbackReason: "",
  };
}

function readResearchQuery(rawArgs: unknown): string {
  return readString(isRecord(rawArgs) ? rawArgs : null, "query");
}

function companyKeysMentioned(
  query: string,
  pending: readonly PendingIdLookupFallback[],
): string[] {
  const normalizedQuery = normalizeCompanyKey(query);
  if (!normalizedQuery) return [];
  return pending
    .filter(({ key }) => key.length > 0 && normalizedQuery.includes(key))
    .map(({ key }) => key);
}

export class IdLookupRoutingPolicy {
  private readonly requested = new Set<IdLookupRequestedField>();
  private completedLookupCount = 0;
  private anonymousRetryRequired = false;
  private readonly retryRequired = new Map<string, string>();
  private readonly pendingFallbacks = new Map<string, PendingIdLookupFallback>();
  private readonly reservedFallbacks = new Set<string>();
  private readonly consumedFallbacks = new Set<string>();
  private readonly disabled: boolean;

  constructor(
    intent: IdLookupRoutingIntent,
    options: { disabled?: boolean } = {},
  ) {
    this.disabled = options.disabled === true;
    for (const field of intent.requestedFields) this.requested.add(field);
  }

  get active(): boolean {
    return !this.disabled && this.requested.size > 0;
  }

  get requestedFields(): IdLookupRequestedField[] {
    return Array.from(this.requested);
  }

  get pendingFallbackCount(): number {
    return this.pendingFallbacks.size;
  }

  get pendingFallbacksSnapshot(): PendingIdLookupFallback[] {
    return Array.from(this.pendingFallbacks.values()).map((pending) => ({
      ...pending,
      missingFields: [...pending.missingFields],
    }));
  }

  /**
   * Registered worksheet lookups own their unresolved-row lifecycle. Passing
   * registerFallback=false records the direct result for routing without
   * creating per-row research debt or an argument retry the model cannot fix.
   */
  recordLookupResult(
    rawArgs: unknown,
    output: string | unknown,
    options: { registerFallback?: boolean } = {},
  ): IdLookupAssessment {
    const registerFallback = options.registerFallback ?? true;
    const assessment = assessIdLookupResult(
      rawArgs,
      output,
      this.requestedFields,
    );
    const key = normalizeCompanyKey(assessment.searchText);

    if (assessment.kind === "retry_lookup") {
      if (!registerFallback) {
        this.completedLookupCount += 1;
        return assessment;
      }
      if (key) {
        this.retryRequired.set(key, assessment.searchText);
      } else {
        this.anonymousRetryRequired = true;
      }
      return assessment;
    }

    this.completedLookupCount += 1;
    this.anonymousRetryRequired = false;
    if (key) {
      for (const retryKey of Array.from(this.retryRequired.keys())) {
        if (key.includes(retryKey) || retryKey.includes(key)) {
          this.retryRequired.delete(retryKey);
        }
      }
    }

    if (assessment.kind === "fallback_required") {
      if (!registerFallback) return assessment;
      const fallbackKey = key || `lookup-${this.completedLookupCount}`;
      if (!this.consumedFallbacks.has(fallbackKey)) {
        this.pendingFallbacks.set(fallbackKey, {
          key: fallbackKey,
          searchText: assessment.searchText || "the requested company",
          missingFields: [...assessment.missingFields],
        });
      }
    } else if (key) {
      this.pendingFallbacks.delete(key);
      this.reservedFallbacks.delete(key);
    }

    return assessment;
  }

  evaluateResearchCall(
    rawArgs: unknown,
    options: { sameTurnHasLookup?: boolean } = {},
  ): IdLookupResearchDecision {
    const query = readResearchQuery(rawArgs);
    const queryFields = extractRequestedIdLookupFields(query);
    const decision = this.decideResearchCall(query, queryFields, options);
    // A blocked call never reaches research, so its wording must not widen the
    // identity this turn has to satisfy. Otherwise a rejected query can force
    // a fallback for a field the user never asked about.
    if (decision.kind !== "blocked") {
      for (const field of queryFields) this.requested.add(field);
    }
    return decision;
  }

  private decideResearchCall(
    query: string,
    queryFields: IdLookupRequestedField[],
    options: { sameTurnHasLookup?: boolean },
  ): IdLookupResearchDecision {
    if (!this.active) return { kind: "normal" };
    if (options.sameTurnHasLookup) {
      return {
        kind: "blocked",
        reason: "lookup_results_unseen",
        nextTool: queryFields.length === 0 ? "research" : null,
      };
    }
    if (this.completedLookupCount === 0) {
      return {
        kind: "blocked",
        reason: "lookup_required",
        nextTool: "id_lookup",
      };
    }

    const pending = this.pendingFallbacksSnapshot;
    if (pending.length === 0) {
      return queryFields.length > 0
        ? {
            kind: "blocked",
            reason: "lookup_already_satisfied",
            nextTool: null,
          }
        : { kind: "normal" };
    }

    let pendingKeys = companyKeysMentioned(query, pending);
    const missingFieldSet = new Set(
      pending.flatMap(({ missingFields }) => missingFields),
    );
    const asksForMissingField = queryFields.some((field) =>
      missingFieldSet.has(field),
    );
    if (!asksForMissingField) {
      return queryFields.length === 0
        ? { kind: "normal" }
        : {
            kind: "blocked",
            reason: "fallback_scope_mismatch",
            nextTool: null,
          };
    }
    if (pendingKeys.length === 0 && pending.length === 1) {
      pendingKeys = [pending[0].key];
    }
    if (pendingKeys.length > 0) {
      const unreservedKeys = pendingKeys.filter(
        (key) => !this.reservedFallbacks.has(key),
      );
      if (unreservedKeys.length === 0) {
        return {
          kind: "blocked",
          reason: "fallback_in_progress",
          nextTool: null,
        };
      }
      const missingFields = Array.from(
        new Set(
          pending
            .filter(({ key }) => unreservedKeys.includes(key))
            .flatMap(({ missingFields: fields }) => fields),
        ),
      );
      return {
        kind: "fallback",
        pendingKeys: unreservedKeys,
        missingFields,
      };
    }

    return {
      kind: "blocked",
      reason: "fallback_scope_mismatch",
      nextTool: null,
    };
  }

  /**
   * Reserve a field-aware fallback synchronously before dispatch. This prevents
   * duplicate calls in one model turn without consuming the fallback yet.
   */
  recordResearchAttempt(decision: IdLookupResearchDecision): void {
    if (decision.kind !== "fallback") return;
    for (const key of decision.pendingKeys) {
      if (this.pendingFallbacks.has(key)) {
        this.reservedFallbacks.add(key);
      }
    }
  }

  /**
   * Consume a reservation only after a usable research answer. Failed or empty
   * research releases it and leaves the original fallback pending.
   */
  recordResearchResult(
    decision: IdLookupResearchDecision,
    usableAnswer: boolean,
  ): void {
    if (decision.kind !== "fallback") return;
    for (const key of decision.pendingKeys) {
      this.reservedFallbacks.delete(key);
      if (usableAnswer && this.pendingFallbacks.has(key)) {
        this.pendingFallbacks.delete(key);
        this.consumedFallbacks.add(key);
      }
    }
  }

  terminalBlock(): IdLookupTerminalBlock | null {
    if (!this.active) return null;
    const requested = this.requestedFields.map((field) => FIELD_LABELS[field]);
    if (this.anonymousRetryRequired || this.retryRequired.size > 0) {
      const companies = Array.from(this.retryRequired.values());
      return {
        issue: "id_lookup_retry_required",
        nextTool: "id_lookup",
        instruction:
          "The previous id_lookup was not attempted because its arguments were invalid. " +
          `Correct the strict arguments and retry id_lookup${companies.length > 0 ? ` for ${companies.join(", ")}` : ""}; do not use research for an input error.`,
      };
    }
    if (this.completedLookupCount === 0) {
      return {
        issue: "id_lookup_required",
        nextTool: "id_lookup",
        instruction:
          `The user requested ${requested.join(", ")}, so call id_lookup before answering or using research. ` +
          "The user does not need to name FactSet or the tool.",
      };
    }
    if (this.pendingFallbacks.size > 0) {
      const pending = this.pendingFallbacksSnapshot.map(
        ({ searchText, missingFields }) =>
          `${searchText}: ${missingFields.map((field) => FIELD_LABELS[field]).join(", ")}`,
      );
      return {
        issue: "id_lookup_fallback_required",
        nextTool: "research",
        instruction:
          "FactSet company lookup did not return the requested field(s) below. " +
          "Call the branded research tool for each pending company and only for these missing facts: " +
          pending.join("; "),
      };
    }
    return null;
  }
}

export function buildBlockedIdLookupResearchOutput(
  decision: Extract<IdLookupResearchDecision, { kind: "blocked" }>,
  retryWith: string | null = decision.nextTool,
): string {
  const messages: Record<typeof decision.reason, string> = {
    lookup_required:
      "Research was skipped because the FactSet company lookup path must run before existing research for this identity request.",
    lookup_results_unseen:
      "Research was skipped because it was emitted in the same turn as company lookup; wait for the lookup result before deciding whether fallback is needed.",
    lookup_already_satisfied:
      "Research was skipped because company lookup already returned the requested identity field or valid candidates.",
    fallback_scope_mismatch:
      "Research was skipped because it did not target a company and requested field that remained unavailable after id_lookup.",
    fallback_in_progress:
      "Research was skipped because the same field-aware fallback was already reserved in this turn.",
  };
  return JSON.stringify({
    ok: false,
    skipped: true,
    error_code: "id_lookup_routing_policy",
    error: messages[decision.reason],
    retryable: retryWith !== null,
    retry_with: retryWith,
  });
}
